import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, ShieldCheck, ArrowRight, Info, X } from "lucide-react";

/**
 * People & Access — VAULT Together + Family permission matrix UI.
 *
 * Backed by `services/permissions.py` + `routes/household_permissions.py`.
 * Owner sees every household member; each row expands to a 10-scope grid
 * with a 3-way toggle (None / View / Edit). Owner cannot rewrite their
 * own row — the backend enforces this (returns 400).
 *
 * Rights-summary dialog runs before Save so the Owner sees exactly what
 * the invitee will and won't be able to do (spec §7).
 */

const SCOPES = [
    { key: "payments",         label: "Payments",              blurb: "Shared bills, EMIs and household payments." },
    { key: "subscriptions",    label: "Subscriptions",         blurb: "Streaming, utilities, shared memberships." },
    { key: "documents",        label: "Documents",             blurb: "Family document vault — receipts, statements." },
    { key: "insurance",        label: "Insurance",             blurb: "Policies covering the household." },
    { key: "warranties",       label: "Warranties",            blurb: "Product warranties on shared items." },
    { key: "calendar",         label: "Family calendar",       blurb: "Renewals, dues, birthdays, appointments." },
    { key: "responsibilities", label: "Shared responsibilities", blurb: "Who owes what, who's paying which bill." },
    { key: "travel",           label: "Travel",                blurb: "Boarding passes and trips explicitly shared." },
    { key: "assets",           label: "Shared assets",         blurb: "Jointly-owned property, funds, valuables." },
    { key: "liabilities",      label: "Shared liabilities",    blurb: "Joint loans, EMIs, dues." },
];
const LEVELS = ["none", "view", "edit"];
const LEVEL_COPY = { none: "None", view: "View", edit: "Edit" };

function LevelToggle({ value, onChange, disabled }) {
    return (
        <div className="inline-flex rounded-full border border-stone-200 overflow-hidden">
            {LEVELS.map((lvl) => (
                <button
                    key={lvl}
                    disabled={disabled}
                    onClick={() => onChange(lvl)}
                    data-testid={`level-${lvl}`}
                    className={`px-3 py-1 text-[11px] font-semibold transition-colors ${
                        value === lvl
                            ? (lvl === "edit" ? "bg-emerald-500 text-white"
                                : lvl === "view" ? "bg-amber-400 text-stone-900"
                                : "bg-stone-200 text-stone-700")
                            : "bg-white text-stone-500 hover:bg-stone-50"
                    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                    {LEVEL_COPY[lvl]}
                </button>
            ))}
        </div>
    );
}

function RightsSummary({ member, current, next, onCancel, onConfirm, busy }) {
    const canScopes = SCOPES.filter((s) => next[s.key] !== "none");
    const notScopes = SCOPES.filter((s) => next[s.key] === "none");
    const changes = SCOPES.filter((s) => (current?.[s.key] || "none") !== next[s.key]);
    if (!member) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" data-testid="rights-summary-dialog">
                <header className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
                    <ShieldCheck size={16} className="text-amber-600" />
                    <div className="text-[14.5px] font-semibold text-stone-900">Confirm access for {member.name || member.email}</div>
                    <button onClick={onCancel} className="ml-auto rounded-full p-1 hover:bg-stone-100" aria-label="Close">
                        <X size={16} />
                    </button>
                </header>
                <div className="p-5 space-y-4 text-[13px]">
                    <p className="text-stone-500">
                        {changes.length === 0
                            ? "Nothing to save — permissions are unchanged."
                            : `${changes.length} change${changes.length === 1 ? "" : "s"} pending.`}
                    </p>
                    {canScopes.length > 0 && (
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">They will be able to</div>
                            <ul className="space-y-1">
                                {canScopes.map((s) => (
                                    <li key={s.key} className="flex items-center gap-2 text-stone-800">
                                        <span className={next[s.key] === "edit" ? "text-emerald-700" : "text-amber-700"}>✓</span>
                                        {s.label} <span className="text-stone-400">·</span> <span className="uppercase text-[10.5px] font-semibold">{next[s.key]}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {notScopes.length > 0 && (
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-widest text-stone-600 mb-1.5">They will not be able to</div>
                            <ul className="space-y-1 text-stone-500">
                                {notScopes.map((s) => (
                                    <li key={s.key} className="flex items-center gap-2">
                                        <span className="text-stone-300">✗</span> {s.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 flex items-start gap-2 text-[11.5px] text-stone-600">
                        <Info size={12} className="mt-0.5" />
                        <div>Your private cards, bank accounts, expenses, income and personal documents remain private. This grid only controls shared/household records.</div>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button onClick={onCancel} className="text-[12.5px] text-stone-600 hover:text-stone-900 px-3 py-2">Cancel</button>
                        <button
                            onClick={onConfirm}
                            disabled={busy || changes.length === 0}
                            data-testid="rights-summary-confirm"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-stone-200 disabled:text-stone-500 text-stone-900 text-[13px] font-semibold"
                        >
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MemberRow({ member }) {
    const [current, setCurrent] = useState(null);
    const [draft, setDraft] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const dirty = useMemo(() => {
        if (!current || !draft) return false;
        return SCOPES.some((s) => (current[s.key] || "none") !== (draft[s.key] || "none"));
    }, [current, draft]);

    const load = async () => {
        try {
            const r = await api.get(`/household/members/${member.id}/permissions`);
            setCurrent(r.data.permissions);
            setDraft({ ...r.data.permissions });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load permissions");
        }
    };

    useEffect(() => { if (expanded && !current) load(); }, [expanded]);

    const commit = async () => {
        setBusy(true);
        try {
            const updates = {};
            SCOPES.forEach((s) => {
                if ((current[s.key] || "none") !== draft[s.key]) updates[s.key] = draft[s.key];
            });
            const r = await api.patch(`/household/members/${member.id}/permissions`, { updates });
            setCurrent(r.data.permissions);
            setDraft({ ...r.data.permissions });
            setConfirming(false);
            toast.success("Access updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-stone-200 rounded-2xl bg-white overflow-hidden" data-testid={`member-row-${member.id}`}>
            <button
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50"
                data-testid={`member-toggle-${member.id}`}
            >
                <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center text-[12px] font-bold text-amber-800">
                    {(member.name || member.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-stone-900 truncate">{member.name || member.email}</div>
                    <div className="text-[11.5px] text-stone-500 truncate">{member.role} · {member.email} · {member.status || "Active"}</div>
                </div>
                <span className="text-[11px] font-semibold text-stone-500">{expanded ? "Close" : "Manage access"}</span>
            </button>

            {expanded && (
                <div className="border-t border-stone-100 p-4 space-y-2">
                    {!draft ? (
                        <div className="text-[12.5px] text-stone-500 py-6 text-center">Loading permissions…</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-2">
                                {SCOPES.map((s) => (
                                    <div key={s.key} className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-0" data-testid={`scope-row-${s.key}`}>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-semibold text-stone-900">{s.label}</div>
                                            <div className="text-[11.5px] text-stone-500">{s.blurb}</div>
                                        </div>
                                        <LevelToggle
                                            value={draft[s.key] || "none"}
                                            onChange={(v) => setDraft({ ...draft, [s.key]: v })}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between pt-2">
                                <div className="text-[11.5px] text-stone-500 inline-flex items-center gap-1">
                                    <Info size={11} /> Private records (cards, accounts, expenses, income) stay private always.
                                </div>
                                <button
                                    onClick={() => setConfirming(true)}
                                    disabled={!dirty}
                                    data-testid={`member-save-${member.id}`}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:bg-stone-200 disabled:text-stone-500 text-stone-900 text-[13px] font-semibold"
                                >
                                    Review &amp; save <ArrowRight size={12} />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {confirming && (
                <RightsSummary
                    member={member}
                    current={current}
                    next={draft}
                    onCancel={() => setConfirming(false)}
                    onConfirm={commit}
                    busy={busy}
                />
            )}
        </div>
    );
}

export default function PeopleAccess() {
    const [members, setMembers] = useState(null);
    useEffect(() => {
        api.get("/household/members")
            .then((r) => setMembers(r.data.members || []))
            .catch(() => setMembers([]));
    }, []);
    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="people-access-page">
            <header className="mb-6">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Together · Family</div>
                <h1 className="text-3xl font-semibold text-stone-900 mt-1 inline-flex items-center gap-2">
                    <Users size={22} /> People &amp; access
                </h1>
                <p className="text-[13px] text-stone-500 mt-1">
                    Configure exactly what each person can see and edit. Sharing a life doesn&rsquo;t mean sharing everything.
                </p>
            </header>
            {members === null ? (
                <div className="text-[13px] text-stone-500 py-6">Loading household…</div>
            ) : members.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/60 p-8 text-center">
                    <div className="text-base font-semibold text-stone-900 mb-1">No shared members yet</div>
                    <p className="text-[13px] text-stone-500">Invite a partner or family member from VAULT Together to configure their access here.</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="member-list">
                    {members.map((m) => <MemberRow key={m.id} member={m} />)}
                </div>
            )}
        </div>
    );
}
